"use client";
import { toast } from "@rallly/ui/sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { useAction } from "next-safe-action/hooks";
import { useTranslation } from "@/i18n/client";
import type { AppErrorCode } from "@/lib/errors/app-error";

export const useSafeAction: typeof useAction = (action, options) => {
  const router = useRouter();
  return useSafeActionBase(action, {
    ...options,
    onSuccess: (args) => {
      router.refresh();
      options?.onSuccess?.(args);
    },
  });
};

/**
 * For actions that call refresh() or revalidatePath: the action response
 * already carries the re-rendered page, so a router.refresh() would be a
 * second render.
 */
export const useRevalidatingSafeAction: typeof useAction = (action, options) =>
  useSafeActionBase(action, options);

const useSafeActionBase: typeof useAction = (action, options) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useAction(action, {
    ...options,
    onSuccess: (args) => {
      // Same blanket invalidation the tRPC mutation override does, so a
      // client query never shows stale data after a write.
      void queryClient.invalidateQueries();
      options?.onSuccess?.(args);
    },
    onError: (args) => {
      const { error } = args;
      if (error.serverError) {
        let translatedDescription = "An unexpected error occurred";

        switch (error.serverError as AppErrorCode) {
          case "UNAUTHORIZED":
            translatedDescription = t("actionErrorUnauthorized", {
              defaultValue: "You are not authorized to perform this action",
            });
            break;
          case "NOT_FOUND":
            translatedDescription = t("actionErrorNotFound", {
              defaultValue: "The resource was not found",
            });
            break;
          case "FORBIDDEN":
            translatedDescription = t("actionErrorForbidden", {
              defaultValue: "You are not allowed to perform this action",
            });
            break;
          case "INTERNAL_SERVER_ERROR":
            translatedDescription = t("actionErrorInternalServerError", {
              defaultValue: "An internal server error occurred",
            });
            break;
          case "TOO_MANY_REQUESTS":
            translatedDescription = t("actionErrorTooManyRequests", {
              defaultValue: "You are making too many requests",
            });
            break;
          case "PAYMENT_REQUIRED":
            translatedDescription = t("actionErrorPaymentRequired", {
              defaultValue: "You need to upgrade to perform this action",
            });
            break;
          case "SERVICE_UNAVAILABLE":
            translatedDescription = t("actionErrorServiceUnavailable", {
              defaultValue:
                "The service required to perform this action is not available",
            });
            break;
          case "PAYLOAD_TOO_LARGE":
            translatedDescription = t("actionErrorPayloadTooLarge", {
              defaultValue:
                "The file you uploaded is too large. Please try a smaller file.",
            });
            break;
        }

        toast.error(translatedDescription);
      }
      options?.onError?.(args);
    },
  });
};
